import { Button, Card, CardContent, FilterBar, PageHeader, QueueTabs, StatusChip } from "@jod/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { isOrderPastSla } from "../../features/ops/alerts.ts";
import { hasPermission } from "../../features/ops/permissions.ts";
import { useOrders, useUpdateOrderStatus } from "../../features/ops/queries.ts";
import type { OrderStatus, StaffPermission } from "../../features/ops/types.ts";
import { getStaffPrincipal } from "../../lib/auth-session.ts";
import { formatMoney } from "../../lib/format.ts";

export const Route = createFileRoute("/_app/orders")({ component: OrdersScreen });

const statuses: OrderStatus[] = [
  "pending",
  "accepted",
  "preparing",
  "ready",
  "served",
  "cancelled",
  "rejected",
];
const stations = ["all", "bar", "kitchen", "runner", "cashier"];
const tone: Record<OrderStatus, "warning" | "info" | "success" | "neutral" | "danger"> = {
  pending: "warning",
  accepted: "info",
  preparing: "info",
  ready: "success",
  served: "success",
  cancelled: "neutral",
  rejected: "danger",
};
const next: Partial<Record<OrderStatus, OrderStatus>> = {
  pending: "accepted",
  accepted: "preparing",
  preparing: "ready",
  ready: "served",
};
// FR-STAFF-040/041, FR-ROLE-* §3: "pending" -> "accepted" is the accept/reject
// action (waiter/bartender/floor-manager); everything after that is the
// fulfilment-status action (also granted to runner).
const nextActionPermission: Partial<Record<OrderStatus, StaffPermission>> = {
  pending: "order:accept",
  accepted: "order:fulfil",
  preparing: "order:fulfil",
  ready: "order:fulfil",
};

function OrdersScreen() {
  const navigate = useNavigate();
  const principal = getStaffPrincipal();
  const [station, setStation] = useState("all");
  const { data: orders = [] } = useOrders(station);
  const update = useUpdateOrderStatus();
  const tabs = useMemo(
    () =>
      stations.map((key) => ({
        key,
        label: key === "all" ? "All" : key,
        count: key === station ? orders.length : undefined,
      })),
    [orders.length, station],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Orders / ออเดอร์"
        subtitle="Queue grouped by canonical order lifecycle with role/station filtering."
      />
      <FilterBar>
        <QueueTabs tabs={tabs} active={station} onChange={setStation} />
      </FilterBar>
      <div className="grid gap-4 xl:grid-cols-7">
        {statuses.map((status) => {
          const column = orders.filter((order) => order.status === status);
          return (
            <section key={status} className="space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="font-display font-semibold text-sm capitalize">{status}</h2>
                <StatusChip tone={tone[status]}>{column.length}</StatusChip>
              </div>
              {column.map((order) => {
                const pastSla = isOrderPastSla(order.placedAt, order.status);
                const requiredPermission = nextActionPermission[order.status];
                const canAdvance =
                  !requiredPermission || hasPermission(principal, requiredPermission);
                return (
                  <Card
                    key={order.id}
                    className={pastSla ? "min-w-0 border-destructive" : "min-w-0"}
                  >
                    <CardContent className="space-y-3 p-4">
                      <button
                        type="button"
                        className="block w-full text-left"
                        onClick={() =>
                          navigate({ to: "/orders/$orderId", params: { orderId: order.id } })
                        }
                      >
                        <span className="font-medium text-sm">{order.tableLabel}</span>
                        <span className="ml-2 text-muted-foreground text-xs">{order.placedAt}</span>
                      </button>
                      <div className="flex flex-wrap gap-1">
                        <StatusChip tone="neutral">{order.station}</StatusChip>
                        {order.source === "gift" ? (
                          <StatusChip tone="info">gift-originated</StatusChip>
                        ) : null}
                        {pastSla ? (
                          <StatusChip tone="danger">⚠ SLA breach / เกินเวลา</StatusChip>
                        ) : null}
                      </div>
                      <ul className="space-y-1 text-sm">
                        {order.lines.map((line) => (
                          <li key={line.id} className="flex justify-between gap-2">
                            <span>
                              {line.qty}× {line.name}
                            </span>
                            <span>{formatMoney(line.qty * line.priceMinor)}</span>
                          </li>
                        ))}
                      </ul>
                      <p className="font-semibold text-sm">{formatMoney(order.totalMinor)}</p>
                      {order.conflict ? (
                        <p className="text-destructive text-xs">{order.conflict}</p>
                      ) : null}
                      {next[order.status] ? (
                        <Button
                          className="min-h-11 w-full"
                          size="sm"
                          disabled={!canAdvance}
                          title={canAdvance ? undefined : "Requires additional staff permission"}
                          onClick={() => {
                            const nextStatus = next[order.status];
                            if (nextStatus)
                              update.mutate({ orderId: order.id, status: nextStatus });
                          }}
                        >
                          Mark {next[order.status]}
                        </Button>
                      ) : null}
                    </CardContent>
                  </Card>
                );
              })}
            </section>
          );
        })}
      </div>
    </div>
  );
}
